import { usePage } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import { useState, useEffect } from 'react'

export default function FlashMessages() {
  const { flash } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const [visible, setVisible] = useState({ notice: false, alert: false })

  // Show on new flash via adjust-during-render (react.dev "adjusting state
  // when props change") so the effects below only own the dismiss timers.
  //
  // Keyed on flash.id, NOT on the message text. Inertia's useForm.post defaults
  // to preserveState, so this component is not remounted between submissions
  // and prevFlash survives them. Comparing text therefore swallowed a repeat of
  // the SAME message — a user retrying a refused login saw the alert once, then
  // nothing, which reads as a dead button.
  const [prevId, setPrevId] = useState<string | null | undefined>(undefined)
  if (flash.id !== prevId) {
    setPrevId(flash.id)
    setVisible((v) => ({
      notice: flash.notice ? true : v.notice,
      alert: flash.alert ? true : v.alert,
    }))
  }

  // Both timers key on flash.id for the same reason the display does: a repeat
  // of an identical message is a NEW flash and needs its own dismiss window.
  // Keying on the text would leave the second one on screen forever, because
  // the dep never changes and the effect never re-runs.
  useEffect(() => {
    if (flash.notice) {
      const timer = setTimeout(() => setVisible((v) => ({ ...v, notice: false })), 5000)
      return () => clearTimeout(timer)
    }
  }, [flash.id, flash.notice])

  useEffect(() => {
    if (flash.alert) {
      const timer = setTimeout(() => setVisible((v) => ({ ...v, alert: false })), 8000)
      return () => clearTimeout(timer)
    }
  }, [flash.id, flash.alert])

  if (!visible.notice && !visible.alert) return null

  return (
    <div className="toast toast-top toast-end z-50">
      {visible.notice && flash.notice && (
        <div role="alert" className="alert alert-soft alert-success text-sm shadow-lg">
          <span className="iconify lucide--check-circle size-4" />
          <span>{flash.notice}</span>
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => setVisible((v) => ({ ...v, notice: false }))}
          >
            <span className="iconify lucide--x size-3" />
          </button>
        </div>
      )}
      {visible.alert && flash.alert && (
        <div role="alert" className="alert alert-soft alert-error text-sm shadow-lg">
          <span className="iconify lucide--alert-circle size-4" />
          <span>{flash.alert}</span>
          <button
            className="btn btn-ghost btn-xs"
            onClick={() => setVisible((v) => ({ ...v, alert: false }))}
          >
            <span className="iconify lucide--x size-3" />
          </button>
        </div>
      )}
    </div>
  )
}
